import React, { useState, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Typography, CircularProgress } from '@material-ui/core';
import {
  Terminal,
  GitBranch,
  Layers,
  Zap,
  Shield,
  Globe,
  KeyRound,
} from 'lucide-react';
import { FlowCanvas } from './FlowCanvas';
import {
  SignInPageProps,
  useApi,
  ApiRef,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';

// Storage key used by Backstage's default SignInPage for session restoration
const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    background: '#0a0a0a',
  },
  leftPanel: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      position: 'relative',
      overflow: 'hidden',
      background: '#0a0a0a',
      borderRight: '1px solid #181818',
    },
  },
  logoContainer: {
    position: 'absolute',
    top: 32,
    left: 40,
    zIndex: 10,
  },
  logo: {
    height: 60,
    width: 'auto',
    padding: 8,
  },
  bottomContent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '0 48px 56px',
    zIndex: 10,
    background:
      'linear-gradient(to top, rgba(12,12,12,0.95) 0%, rgba(12,12,12,0.7) 60%, transparent 100%)',
  },
  headline: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.4,
    marginBottom: 8,
  },
  headlineAccent: {
    color: '#F0A31C',
  },
  subheadline: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 13,
    lineHeight: 1.7,
    maxWidth: 340,
    marginBottom: 20,
  },
  featureChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  featureChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 20,
    border: '1px solid rgba(240,163,28,0.2)',
    background: 'rgba(240,163,28,0.05)',
    backdropFilter: 'blur(4px)',
  },
  featureChipIcon: {
    width: 14,
    height: 14,
    color: '#F0A31C',
  },
  featureChipLabel: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    background: '#111111',
    [theme.breakpoints.up('lg')]: {
      width: 440,
    },
    [theme.breakpoints.up('xl')]: {
      width: 480,
    },
  },
  topAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: 'linear-gradient(90deg, transparent, #F0A31C, transparent)',
  },
  mobileLogoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 48,
    paddingBottom: 8,
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  formContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '48px 32px',
    [theme.breakpoints.up('sm')]: {
      padding: '48px 48px',
    },
  },
  title: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.25,
    marginBottom: 4,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 13,
    marginBottom: 32,
  },
  googlePanel: {
    borderRadius: 12,
    padding: 20,
    textAlign: 'center',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    marginBottom: 20,
  },
  googleIconContainer: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#232323',
    margin: '0 auto 12px',
  },
  googleButton: {
    width: '100%',
    height: 44,
    borderRadius: 8,
    background: '#fff',
    color: '#1a1a1a',
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: '#f5f5f5',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: '#2a2a2a',
  },
  dividerText: {
    fontSize: 11,
    color: '#444',
    letterSpacing: '0.08em',
  },
  ssoButton: {
    width: '100%',
    height: 44,
    borderRadius: 8,
    background: 'transparent',
    color: '#F0A31C',
    border: '1px solid rgba(240,163,28,0.2)',
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      background: 'rgba(240,163,28,0.05)',
      borderColor: 'rgba(240,163,28,0.4)',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
  footer: {
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid #1e1e1e',
  },
  footerText: {
    textAlign: 'center',
    fontSize: 11,
    color: '#444',
    lineHeight: 1.7,
  },
  footerLink: {
    color: '#F0A31C',
    cursor: 'pointer',
  },
  version: {
    paddingBottom: 24,
    display: 'flex',
    justifyContent: 'center',
  },
  versionText: {
    fontSize: 10,
    color: '#333',
    letterSpacing: '0.1em',
  },
  error: {
    fontSize: 12,
    color: '#ef4444',
    marginTop: 16,
    textAlign: 'center',
  },
}));

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" style={{ width: 16, height: 16 }} fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const FEATURE_CHIPS = [
  { icon: Layers, label: 'Service Catalog' },
  { icon: Terminal, label: 'Self-service Docs' },
  { icon: GitBranch, label: 'CI/CD Integration' },
  { icon: Shield, label: 'Access Control' },
  { icon: Zap, label: 'Real-time Metrics' },
  { icon: Globe, label: 'Multi-workspace' },
];

type OAuthApi = ProfileInfoApi & BackstageIdentityApi & SessionApi;

interface Provider {
  id: string;
  title: string;
  message?: string;
  apiRef: ApiRef<OAuthApi>;
}

export interface CustomSignInPageProps extends SignInPageProps {
  providers: Array<Provider | 'guest'>;
  googleApiRef: ApiRef<OAuthApi>;
  externalSsoApiRef: ApiRef<OAuthApi>;
}

export const CustomSignInPage = (props: CustomSignInPageProps) => {
  const classes = useStyles();
  const { onSignInSuccess, googleApiRef, externalSsoApiRef } = props;

  const [loading, setLoading] = useState<'google' | 'sso' | 'restoring' | null>(
    'restoring',
  );
  const [error, setError] = useState('');
  const [showLoginPage, setShowLoginPage] = useState(false);

  // Always call hooks unconditionally (React rules of hooks)
  const googleApi = useApi(googleApiRef);
  const externalSsoApi = useApi(externalSsoApiRef);

  // Helper to create identity using Backstage's UserIdentity class
  const createIdentity = useCallback(async (authApi: OAuthApi) => {
    const identityResponse = await authApi.getBackstageIdentity();
    if (!identityResponse) {
      throw new Error('No identity found');
    }
    const profile = await authApi.getProfile();
    return UserIdentity.create({
      identity: identityResponse.identity,
      authApi,
      profile,
    });
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const savedProvider = localStorage.getItem(PROVIDER_STORAGE_KEY);

      if (!savedProvider) {
        setLoading(null);
        setShowLoginPage(true);
        return;
      }

      try {
        let authApi: OAuthApi | null = null;

        // Determine which API to use based on saved provider
        if (
          savedProvider === 'google-auth-provider' ||
          savedProvider === 'google'
        ) {
          authApi = googleApi;
        } else if (savedProvider === 'oauth2' || savedProvider === 'zitadel') {
          authApi = externalSsoApi;
        } else if (savedProvider === 'guest') {
          onSignInSuccess(UserIdentity.createGuest());
          return;
        }

        if (!authApi) {
          localStorage.removeItem(PROVIDER_STORAGE_KEY);
          setLoading(null);
          setShowLoginPage(true);
          return;
        }

        // Try to get existing identity (checks if session is still valid)
        const identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });

        if (identityResponse) {
          // Session exists, restore it
          const profile = await authApi.getProfile();
          const identity = UserIdentity.create({
            identity: identityResponse.identity,
            authApi,
            profile,
          });
          onSignInSuccess(identity);
        } else {
          // No valid session, show login page
          localStorage.removeItem(PROVIDER_STORAGE_KEY);
          setLoading(null);
          setShowLoginPage(true);
        }
      } catch (err) {
        // Session restoration failed, show login page
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        setLoading(null);
        setShowLoginPage(true);
      }
    };

    restoreSession();
  }, [googleApi, externalSsoApi, onSignInSuccess]);

  const handleGoogleLogin = async () => {
    setLoading('google');
    setError('');

    try {
      // Store provider before sign-in attempt
      localStorage.setItem(PROVIDER_STORAGE_KEY, 'google-auth-provider');

      const identityResponse = await googleApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        throw new Error('Google provider is not configured for sign-in');
      }

      const identity = await createIdentity(googleApi);
      onSignInSuccess(identity);
    } catch (err: any) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      setError(err?.message || 'Google login failed. Please try again.');
      setLoading(null);
    }
  };

  const handleGuestLogin = () => {
    localStorage.setItem(PROVIDER_STORAGE_KEY, 'guest');
    onSignInSuccess(UserIdentity.createGuest());
  };

  const handleExternalSsoLogin = async () => {
    setLoading('sso');
    setError('');

    try {
      // Store provider before sign-in attempt
      localStorage.setItem(PROVIDER_STORAGE_KEY, 'oauth2');

      const identityResponse = await externalSsoApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        throw new Error('External SSO provider is not configured for sign-in');
      }

      const identity = await createIdentity(externalSsoApi);
      onSignInSuccess(identity);
    } catch (err: any) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      setError(err?.message || 'SSO login failed. Please try again.');
      setLoading(null);
    }
  };

  // Show loading spinner while restoring session
  if (loading === 'restoring' && !showLoginPage) {
    return (
      <div
        className={classes.root}
        style={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress style={{ color: '#F0A31C' }} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Left Panel - Flow Canvas */}
      <div className={classes.leftPanel}>
        <FlowCanvas />

        <div className={classes.logoContainer}>
          <img
            src="/konteks-logo-dark.svg"
            alt="Konteks"
            className={classes.logo}
          />
        </div>

        <div className={classes.bottomContent}>
          <Typography className={classes.headline}>
            One portal.
            <br />
            <span className={classes.headlineAccent}>Every component.</span>
          </Typography>
          <Typography className={classes.subheadline}>
            Discover, govern, and ship every service, gateway, library, and
            infrastructure component — from one unified platform.
          </Typography>
          <div className={classes.featureChips}>
            {FEATURE_CHIPS.map((chip, index) => (
              <div key={index} className={classes.featureChip}>
                <chip.icon className={classes.featureChipIcon} />
                <span className={classes.featureChipLabel}>{chip.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className={classes.rightPanel}>
        <div className={classes.topAccent} />

        <div className={classes.mobileLogoContainer}>
          <img
            src="/konteks-logo-dark.svg"
            alt="Konteks"
            className={classes.logo}
          />
        </div>

        <div className={classes.formContainer}>
          <Typography className={classes.title}>Welcome back</Typography>
          <Typography className={classes.subtitle}>
            Sign in to your Konteks workspace
          </Typography>

          {/* Google SSO Panel */}
          <div className={classes.googlePanel}>
            <div className={classes.googleIconContainer}>
              <GoogleIcon />
            </div>
            <Typography
              style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Continue with Google SSO
            </Typography>
            <Typography
              style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}
            >
              Use your organisation's Google Workspace account to access
              Konteks.
            </Typography>
          </div>

          <Button
            fullWidth
            variant="contained"
            disabled={loading === 'google' || loading === 'sso'}
            className={classes.googleButton}
            onClick={handleGoogleLogin}
            disableElevation
          >
            {loading === 'google' ? (
              <CircularProgress size={18} style={{ color: '#666' }} />
            ) : (
              <>
                <GoogleIcon />
                <span style={{ marginLeft: 12 }}>Continue with Google</span>
              </>
            )}
          </Button>

          <div className={classes.divider}>
            <div className={classes.dividerLine} />
            <span className={classes.dividerText}>OR</span>
            <div className={classes.dividerLine} />
          </div>

          <button
            className={classes.ssoButton}
            onClick={handleExternalSsoLogin}
            type="button"
            disabled={loading === 'google' || loading === 'sso'}
          >
            {loading === 'sso' ? (
              <CircularProgress size={18} style={{ color: '#F0A31C' }} />
            ) : (
              <>
                <KeyRound size={16} />
                <span>Sign in with External SSO</span>
              </>
            )}
          </button>

          {error && <Typography className={classes.error}>{error}</Typography>}

          {props.providers.includes('guest') && (
            <button
              className={classes.ssoButton}
              onClick={handleGuestLogin}
              type="button"
              style={{ marginTop: 8, opacity: 0.6 }}
            >
              Continue as Guest
            </button>
          )}

          <div className={classes.footer}>
            <Typography className={classes.footerText}>
              By signing in you agree to Konteks's{' '}
              <span className={classes.footerLink}>Terms of Service</span> and{' '}
              <span className={classes.footerLink}>Privacy Policy</span>
            </Typography>
          </div>
        </div>

        <div className={classes.version}>
          <span className={classes.versionText}>
            Konteks v2.0 · Internal Developer Portal
          </span>
        </div>
      </div>
    </div>
  );
};

export default CustomSignInPage;
